import React from 'react';
import './index.css';
import { Table, Button, Space, Typography, Input, Switch } from 'antd';
import { CopyOutlined, ReloadOutlined, SearchOutlined } from '@ant-design/icons';
import { message } from 'antd';
import type { ColumnsType, TablePaginationConfig } from 'antd/es/table';
import { getUserType } from '../../../helpers/userDetails';
import { formatDate } from '../../../helpers/date';
import moment from 'moment';
import timezone from 'moment-timezone';
interface BatchVouchersTablePropWithReset extends BatchVouchersTableProp {
  onResetVoucher?: (voucherId: number | string) => void;
  onToggleVoucher?: (voucherId: number | string, enable: number) => void;
  onSearch?: (searchBy: 'mac' | 'voucher', value: string) => void;
  onPaginationChange?: (page: number, pageSize: number) => void;
  onSortChange?: (sortBy: string, sortOrder: 'asc' | 'desc' | null) => void;
  pagination?: {
    current: number;
    pageSize: number;
    total: number;
  };
  loading?: boolean;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc' | null;
  searchMac?: string;
  searchVoucher?: string;
}

const BatchVouchersTable: React.FC<BatchVouchersTablePropWithReset> = ({
  vouchers,
  onResetVoucher,
  onToggleVoucher,
  onSearch,
  onPaginationChange,
  onSortChange,
  pagination,
  loading = false,
  sortBy: currentSortBy,
  sortOrder: currentSortOrder,
  searchMac: macSearch = '',
  searchVoucher: voucherSearch = '',
}) => {
  const userRole = getUserType();
  const canResetVoucher = userRole === 'ADMIN' || userRole === 'MANAGER';
  const canToggleVoucher = userRole === 'ADMIN' || userRole === 'MANAGER';

  const handleCopy = (voucherNumber: string) => {
    navigator.clipboard
      .writeText(voucherNumber)
      .then(() => {
        message.success('Voucher number copied to clipboard');
      })
      .catch(() => {
        message.error('Failed to copy voucher number');
      });
  };

  const handleMacSearch = (value: string) => {
    if (onSearch) {
      onSearch('mac', value);
    }
  };

  const handleVoucherSearch = (value: string) => {
    if (onSearch) {
      onSearch('voucher', value);
    }
  };

  const handleTableChange = (paginationConfig: TablePaginationConfig, filters: any, sorter: any) => {
    // Determine the new sort state from the sorter
    const newSortField = sorter && !Array.isArray(sorter) && sorter.field ? sorter.field : undefined;
    const newSortOrder = sorter && !Array.isArray(sorter) && sorter.order 
      ? (sorter.order === 'ascend' ? 'asc' : sorter.order === 'descend' ? 'desc' : null)
      : null;
    
    // Check if sort actually changed by comparing with current state
    const sortChanged = newSortField && newSortOrder && 
      (currentSortBy !== newSortField || currentSortOrder !== newSortOrder);
    
    if (sortChanged && onSortChange) {
      // Sort actually changed - handle sort change
      onSortChange(newSortField, newSortOrder);
    } else if (onPaginationChange && paginationConfig.current && paginationConfig.pageSize) {
      // Only pagination changed (or nothing changed) - handle pagination change
      onPaginationChange(paginationConfig.current, paginationConfig.pageSize);
    }
  };

  const handleToggleVoucher = (checked: boolean, voucherId: number | string) => {
    if (onToggleVoucher) {
      onToggleVoucher(voucherId, checked ? 1 : 0);
    }
  };

  const columns: ColumnsType<BatchVouchersTableDataType> = [
    {
      title: 'Voucher Number',
      key: 'voucherNumber',
      render: (_, record) => (
        <Space>
          <Typography.Text>{record.voucherNumber}</Typography.Text>
          <Button
            type="text"
            size="small"
            icon={<CopyOutlined />}
            onClick={() => handleCopy(record.voucherNumber)}
            title="Copy voucher number"
          />
        </Space>
      ),
    },
    {
      title: 'Enabled',
      key: 'enable',
      width: 100,
      render: (_, record) => {
        const isEnabled = record.enable !== undefined ? record.enable === 1 : true;
        const isExpired = record.isExpired === true;
        return canToggleVoucher && onToggleVoucher ? (
          <Switch
            checked={isEnabled}
            onChange={(checked) => handleToggleVoucher(checked, record.intPkVoucherID)}
            size="small"
            disabled={isExpired}
            title={isExpired ? 'Cannot toggle expired voucher' : ''}
          />
        ) : (
          <span style={{ color: isEnabled ? '#52c41a' : '#ff4d4f' }}>
            {isEnabled ? 'Yes' : 'No'}
          </span>
        );
      },
    },
    {
      title: 'Status',
      dataIndex: 'voucherStatus',
      key: 'voucherStatus',
      render: (_, record) => {
        const isExpired = record.isExpired === true;
        if (isExpired) {
          return <span className="voucher-status-expired">Expired</span>;
        }
        return record.voucherStatus === 'USED' ? (
          <span className="voucher-status-used">Used</span>
        ) : (
          <span className="voucher-status-unused">Unused</span>
        );
      },
    },
    {
      title: 'Created Date',
      dataIndex: 'dateCreated',
      key: 'dateCreated',
      render: (dateCreated: string) => {
        console.log('RAW API date:', dateCreated);
        console.log('JS Date object:', new Date(dateCreated));
        console.log('Moment UTC:', moment.utc(dateCreated).format());
        console.log('Moment Local:', moment.utc(dateCreated).local().format());
        // console.log('Guessed TZ:', moment.tz.guess());

        return <>{formatDate(dateCreated)}</>;
      },
    },
    {
      title: 'Used Date',
      dataIndex: 'voucherUsedDate',
      key: 'voucherUsedDate',
      sorter: true,
      sortOrder:
        currentSortBy === 'voucherUsedDate'
          ? currentSortOrder === 'asc'
            ? 'ascend'
            : currentSortOrder === 'desc'
            ? 'descend'
            : null
          : null,
      render: (voucherUsedDate: string | null) => (voucherUsedDate ? formatDate(voucherUsedDate) : '-'),
    },
    {
      title: 'MAC Address',
      dataIndex: 'userMacAddress',
      key: 'userMacAddress',
      render: (userMacAddress: string | null) => userMacAddress || '-',
    },
    {
      title: 'Expiry Date',
      dataIndex: 'expiryDate',
      key: 'expiryDate',
      sorter: true,
      sortOrder:
        currentSortBy === 'expiryDate'
          ? currentSortOrder === 'asc'
            ? 'ascend'
            : currentSortOrder === 'desc'
            ? 'descend'
            : null
          : null,
      render: (expiryDate: string | null) => (expiryDate ? formatDate(expiryDate) : '-'),
    },
    {
      title: 'Downloaded',
      dataIndex: 'formattedTotalDownloadedBytes',
      key: 'formattedTotalDownloadedBytes',
      sorter: true,
      sortOrder:
        currentSortBy === 'formattedTotalDownloadedBytes'
          ? currentSortOrder === 'asc'
            ? 'ascend'
            : currentSortOrder === 'desc'
            ? 'descend'
            : null
          : null,
      render: (formattedTotalDownloadedBytes: string) => formattedTotalDownloadedBytes || '0 B',
    },
    {
      title: 'Uploaded',
      dataIndex: 'formattedTotalUploadedBytes',
      key: 'formattedTotalUploadedBytes',
      sorter: true,
      sortOrder:
        currentSortBy === 'formattedTotalUploadedBytes'
          ? currentSortOrder === 'asc'
            ? 'ascend'
            : currentSortOrder === 'desc'
            ? 'descend'
            : null
          : null,
      render: (formattedTotalUploadedBytes: string) => formattedTotalUploadedBytes || '0 B',
    },
    {
      title: 'Total Uptime',
      dataIndex: 'formattedTotalUptime',
      key: 'formattedTotalUptime',
      render: (formattedTotalUptime: string) => formattedTotalUptime || '0 seconds',
    },
    ...(canResetVoucher && onResetVoucher
      ? [
          {
            title: 'Actions',
            key: 'actions',
            render: (_: any, record: BatchVouchersTableDataType) => {
              const isEnabled = record.enable !== undefined ? record.enable === 1 : true;
              const isExpired = record.isExpired === true;
              
              if (isExpired) {
                return (
                  <Typography.Text type="secondary" style={{ fontSize: '12px' }}>
                    Cannot reset (expired)
                  </Typography.Text>
                );
              }
              
              if (record.voucherStatus === 'USED' && isEnabled) {
                return (
                  <Button
                    type="link"
                    icon={<ReloadOutlined />}
                    onClick={() => onResetVoucher(record.intPkVoucherID)}
                    danger
                  >
                    Reset to Reuse
                  </Button>
                );
              }
              if (record.voucherStatus === 'USED' && !isEnabled) {
                return (
                  <Typography.Text type="secondary" style={{ fontSize: '12px' }}>
                    Cannot reset (disabled)
                  </Typography.Text>
                );
              }
              return null;
            },
          },
        ]
      : []),
  ];

  const paginationConfig: TablePaginationConfig | false = pagination
    ? {
        current: pagination.current,
        pageSize: pagination.pageSize,
        total: pagination.total,
        showSizeChanger: true,
        pageSizeOptions: ['25', '100', '500', '1000'],
        showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} vouchers`,
      }
    : false;

  return (
    <>
      {/* Search Controls */}
      <div style={{ marginBottom: 16, display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        <Input
          placeholder="Search by MAC Address"
          prefix={<SearchOutlined />}
          value={macSearch}
          onChange={(e) => handleMacSearch(e.target.value)}
          style={{ width: 250 }}
          allowClear
        />
        <Input
          placeholder="Search by Voucher #"
          prefix={<SearchOutlined />}
          value={voucherSearch}
          onChange={(e) => handleVoucherSearch(e.target.value)}
          style={{ width: 250 }}
          allowClear
        />
      </div>

      {/* Table */}
      <div className="table-container-custom">
        <Table
          key={`batch-vouchers-table-${pagination?.current ?? 1}-${pagination?.pageSize ?? 25}`}
          className="antd-table-striped"
          columns={columns}
          dataSource={vouchers}
          pagination={paginationConfig}
          rowKey="intPkVoucherID"
          loading={loading}
          onChange={handleTableChange}
          sortDirections={['ascend', 'descend']}
          rowClassName={(record) => {
            const isEnabled = record.enable !== undefined ? record.enable === 1 : true;
            const isExpired = record.isExpired === true;
            if (isExpired) {
              return 'voucher-expired';
            }
            return !isEnabled ? 'voucher-disabled' : '';
          }}
        />
      </div>
    </>
  );
};

export default BatchVouchersTable;
